
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { SubscriptionTier } from "@/types/subscription";
import { useNavigate } from "react-router-dom";

interface UsePayPalButtonProps {
  onSuccess?: () => void;
}

export const usePayPalButton = ({ onSuccess }: UsePayPalButtonProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const initializeButton = useCallback(async (containerId: string, clientId: string, tier: SubscriptionTier) => {
    console.log('Initializing PayPal button for container:', containerId);
    if (!window.paypal) {
      console.error('PayPal SDK not loaded');
      return;
    }

    try {
      // Wait for the container to be available in the DOM
      let retries = 0;
      const maxRetries = 10;
      const retryInterval = 500;

      const waitForContainer = async (): Promise<Element | null> => {
        const container = document.querySelector(`#${containerId}`);
        if (container) return container;
        
        if (retries >= maxRetries) {
          throw new Error(`Container #${containerId} not found after ${maxRetries} retries`);
        }
        
        retries++;
        await new Promise(resolve => setTimeout(resolve, retryInterval));
        return waitForContainer();
      };

      const container = await waitForContainer();
      if (!container) {
        throw new Error(`Container #${containerId} not found`);
      }

      // Clear any existing buttons
      container.innerHTML = '';

      const buttons = await window.paypal.Buttons({
        style: {
          shape: 'rect',
          color: tier.highlighted ? 'gold' : 'silver',
          layout: 'vertical',
          label: 'subscribe'
        },
        createSubscription: async () => {
          try {
            console.log('Creating PayPal subscription for tier:', tier.name);
            const { data, error } = await supabase.functions.invoke('create-paypal-subscription', {
              body: { planId: tier.paypalPlanId }
            });

            if (error || !data?.id) {
              throw new Error(error?.message || 'Failed to create subscription');
            }

            console.log('PayPal subscription created successfully:', data);
            return data.id;
          } catch (error: any) {
            console.error('Failed to create subscription:', error);
            toast({
              variant: "destructive",
              title: "Subscription Error",
              description: error?.message || 'Failed to create subscription. Please try again.',
            });
            throw error;
          }
        },
        onApprove: async (data: any, actions: any) => {
          console.log('Subscription approved:', data);
          try {
            // Get the subscriber's details from PayPal
            const details = await actions.subscription.get();
            const subscriberEmail = details.subscriber.email_address;
            
            localStorage.setItem('pendingSubscription', JSON.stringify({
              subscriptionId: data.subscriptionID,
              tier: tier.name,
              email: subscriberEmail,
              timestamp: new Date().toISOString()
            }));
            
            navigate('/create-account');
            
            if (onSuccess) {
              onSuccess();
            }
          } catch (error) {
            console.error('Error in subscription approval:', error);
            toast({
              variant: "destructive",
              title: "Error",
              description: "There was a problem activating your subscription. Please contact support.",
            });
          }
        },
        onError: (err: any) => {
          console.error('PayPal button error:', err);
          toast({
            variant: "destructive",
            title: "Error",
            description: "There was a problem processing your subscription. Please try again.",
          });
          setIsInitialized(false);
        },
        onCancel: () => {
          console.log('Subscription cancelled by user');
          toast({
            title: "Subscription Cancelled",
            description: "You've cancelled the subscription process.",
          });
        }
      });

      if (!buttons.isEligible()) {
        console.error('PayPal Buttons not eligible for rendering');
        return;
      }

      await buttons.render(`#${containerId}`);
      console.log('PayPal button rendered successfully for tier:', tier.name);
      setIsInitialized(true);
    } catch (error: any) {
      console.error('Failed to initialize PayPal button:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Failed to initialize payment option.",
      });
      setIsInitialized(false);
      throw error;
    }
  }, [onSuccess, toast, navigate]);

  return {
    isInitialized,
    initializeButton
  };
};
